/**
 * Hand-written domain types.
 *
 * These map to the `orders` schema (see supabase/migrations/).
 */

export type Category =
  | '3-gal BIB'
  | '5-gal BIB'
  | 'Sugar Free'
  | 'Mixer'
  | '24-Pack'
  | 'Gas'
  | 'Other';

export interface CatalogItem {
  id: string;
  qboItemId: number;
  name: string;
  sku: string | null;
  category: Category;
  unit: string;
  listPrice: number;
  qtyOnHand: number;
  entity: 'AS' | 'FF';
  accentColor: string | null;
  tag: string | null;
  orderable: boolean;
  active: boolean;
  // image_url = the seal / can photo (flavor identifier).
  // bib_image_url = the BIB box photo, when applicable. UI composites them.
  imageUrl: string | null;
  imageThumbUrl: string | null;
  bibImageUrl: string | null;
  description: string | null;
  sfItemId: string | null;
  sfItemCode: string | null;
  sfItemName: string | null;
  displayName: string | null;
  packSize: string | null;
  volumeOz: number | null;
  weightLbs: number | null;
  containerType: string | null;
  dimensionsIn: string | null;
}

export interface Customer {
  id: string;
  qboCustomerId: number;
  name: string;
  accountNumber: string | null;
  paymentTerms: string;
  creditLimit: number | null;
  onCreditHold: boolean;
  active: boolean;
  /** Chain accounts bill centrally → exempt from the onboarding card-on-file
   *  rule (orders.customers.features.card_on_file_exempt). Default false. */
  cardOnFileExempt: boolean;
  /** Customer-default delivery schedule (orders.customers.delivery_schedule
   *  jsonb) — parse with parseDeliverySchedule from lib/deliverySchedule. */
  deliverySchedule: unknown;
}

export interface CustomerLocation {
  id: string;
  customerId: string;
  locationCode: string | null;
  displayName: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  deliveryNotes: string | null;
  active: boolean;
  /** Per-location delivery-schedule override (jsonb) — effective schedule is
   *  location ?? customer (see effectiveSchedule in lib/deliverySchedule). */
  deliverySchedule: unknown;
}

export interface CustomerUser {
  id: string;
  customerId: string;
  fullName: string;
  role: 'member' | 'ap' | 'admin';
  defaultLocationId: string | null;
  phone: string | null;
}

export interface AccountSummary {
  customerId: string;
  name: string;
  paymentTerms: string;
  onCreditHold: boolean;
  creditLimit: number | null;
  openBalance: number;
  openInvoiceCount: number;
  oldestDaysPastDue: number;
  pastDueBalance: number;
}

export type OrderStatus =
  | 'draft'
  | 'submitted'
  | 'accepted'
  | 'scheduled'
  | 'out_for_delivery'
  | 'delivered'
  | 'invoiced'
  | 'cancelled'
  | 'rejected';

export interface CartLine {
  catalogItemId: string;
  qboItemId: number;
  itemName: string;
  sku: string | null;
  quantity: number;
  unitPrice: number;
  listPrice: number;
}

export type CustomerFileKind =
  | 'contract'
  | 'invoice'
  | 'delivery_photo'
  | 'service_doc'
  | 'other';

export interface CustomerFile {
  id: string;
  customerId: string;
  uploadedBy: string | null;
  orderId: string | null;
  bucket: string;
  storagePath: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number | null;
  kind: CustomerFileKind;
  notes: string | null;
  isCustomerVisible: boolean;
  createdAt: string;
  updatedAt: string;
}

// "APT" is the Alameda Point product-line prefix on flavor names. Customer-
// facing surfaces DROP it entirely — "APT Pineapple" reads as just "Pineapple"
// (Sky, 2026-07-09; previously it expanded to "Alameda Point Pineapple").
// Display-ONLY: we never touch item.name itself, which is the QBO/SF master
// header sent verbatim on orders (must stay "APT …").
function stripBrandPrefix(name: string): string {
  const stripped = name.replace(/\b(?:APT|Alameda Point)\b\s*/g, '').trim();
  return stripped.length > 0 ? stripped : name;
}

export function displayName(item: CatalogItem): string {
  const raw =
    item.displayName?.trim() ||
    item.sfItemName?.trim() ||
    item.name;
  return stripBrandPrefix(raw);
}

/**
 * Cylinder pickup/return items (PU* skus + the generic PICK / SVC-PICKUP
 * rows). Hidden from the browsing catalog — they enter an order only through
 * the guided tank-return flow on the cart's submit step.
 */
export function isPickupItem(item: Pick<CatalogItem, 'sku' | 'name'>): boolean {
  const sku = (item.sku ?? '').toUpperCase();
  return (
    sku.startsWith('PU') ||
    sku === 'PICK' ||
    sku === 'SVC-PICKUP' ||
    item.name.toUpperCase().startsWith('PICK UP')
  );
}

export function shortSpecs(item: CatalogItem): string | null {
  const parts: string[] = [];
  if (item.volumeOz !== null) parts.push(`${formatNumber(item.volumeOz)} fl oz`);
  if (item.weightLbs !== null) parts.push(`${formatNumber(item.weightLbs)} lbs`);
  if (item.dimensionsIn) parts.push(item.dimensionsIn);
  return parts.length ? parts.join(' · ') : null;
}

function formatNumber(n: number): string {
  return Number.isInteger(n) ? n.toString() : n.toFixed(1);
}
